import { Fragment, h } from "preact";
import { useState } from "preact/hooks";

import tracking from "../tracking/tracking";
import { replaceVariables } from "../config/germanVariables";
import {
  classNames,
  firstCharUppercase,
  getText,
  getValue,
  humanFileSize,
  renderHtml,
  renderUserHtml,
  replaceRedactions,
  stringTofileName,
} from "../helpers";
import i18n from "../i18n";
import { sortWorkExperiences } from "../talentHelpers";
import { showCompensationRange } from "../services/compensationRange";
import usePreviewClickHandler from "../hooks/usePreviewClickHandler";
import { selectCanSeeTalentPool } from "../store";

import AddEditExperienceModal from "./AddEditExperienceModal.jsx";
import CircularButton from "./CircularButton.jsx";
import CompensationRange from "./CompensationRange.jsx";
import CountryFlag from "./CountryFlag.jsx";
import ListItem from "./ListItem.jsx";
import QualificationDisplay from "./QualificationDisplay.jsx";
import Rating from "./Rating.jsx";
import RedactedInfo from "./RedactedInfo.jsx";
import Tag from "./Tag.jsx";
import WorkExperience from "./talentDetails/WorkExperience.jsx";

export default function TalentDetails({
  previewMode,
  addOrUpdateExperience,
  deleteExperience,
  user,
  isSelf,
}) {
  const eligibilityPrefix = isSelf
    ? "component.talent_details.b2c."
    : "component.talent_details.b2b.";
  let elibilityText = ``;
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [editExperience, setEditExperience] = useState(null);
  // TODO: add different texts when user is not watching himself

  switch (user.eligibilityProduct) {
    case "german_citizen_in_germany":
      elibilityText = i18n.t(`${eligibilityPrefix}german_citizen_in_germany`, {
        name: user.name,
      });
      break;
    case "§6_visa_free":
    case "§18a_occupation":
    case "§18b_blue_card":
    case "§18b_blue_card_developers":
    case "§18g_blue_card":
    case "§18g_blue_card_1_5":
    case "§18g_blue_card_1_2_1":
    case "§18g_blue_card_1_2_2":
    case "§18g_blue_card_2":
    case "§19c_ict_diverse":
    case "not_mvp":
      elibilityText = i18n.t(`${eligibilityPrefix}${user.eligibilityProduct}`, {
        name: user.name,
      });
      break;
    case "with_long_term_germany_resicency_permit":
      elibilityText = i18n.t(
        `${eligibilityPrefix}with_long_term_germany_resicency_permit`,
        {
          name: user.name,
          german_residency_title: getText(user.germanResidencyTitle),
        }
      );
      break;
    case "with_germany_resicency_permit":
      elibilityText = i18n.t(
        `${eligibilityPrefix}with_germany_resicency_permit`,
        {
          name: user.name,
          german_residency_title: getText(user.germanResidencyTitle),
          german_residency_expiry_date: getText(user.germanResidencyExpiryDate),
        }
      );
      break;

    default: // Others don't show anything
  }

  function showCefr() {
    if (
      !user.cefrGerman ||
      user.cefrGerman === "unknown" ||
      user.cefrGerman === "none"
    ) {
      return "";
    }

    return i18n.t("component.talent_details.cefr", {
      value: user.cefrGerman,
    });
  }
  const cefr = showCefr();

  const { handleClickWhenLoggedIn } = usePreviewClickHandler(
    selectCanSeeTalentPool
  );

  function trackResumeClick() {
    tracking.track(previewMode ? "resume.downloadPreview" : "resume.download", {
      talent_id: user.id,
    });
  }

  const additionalInfo0 = user.preferredCities.length > 0 && (
    <div class="mb-24">
      <p class="t-body3 mb-8">{i18n.t("profile_details.pref_cities")}</p>
      {user.preferredCities
        .sort((a, b) => a.localeCompare(b))
        .map((city) => (
          <Tag grey noHover class="mr-12">
            {city}
          </Tag>
        ))}
    </div>
  );
  const additionalInfo1 =
    typeof user.industries === "string" ? (
      <div class="mb-24">
        <p class="t-body3 mb-8">{i18n.t("profile_details.pref_industries")}</p>

        {user.industries === "no_preferred" ? (
          <p class="t-body4 text-dimmed mt-0">
            {i18n.t("profile_details.no_pref_industries")}
          </p>
        ) : (
          <Tag grey noHover class="mr-12">
            {user.industries}
          </Tag>
        )}
      </div>
    ) : (
      user.industries.length > 0 && (
        <div class="mb-24">
          <p class="t-body3 mb-8">
            {i18n.t("profile_details.pref_industries")}
          </p>
          {user.industries
            .sort((a, b) => a.localeCompare(b))
            .map((industry) => (
              <Tag grey noHover class="mr-12">
                {industry}
              </Tag>
            ))}
        </div>
      )
    );

  const additionalInfo2 = !!user.noticePeriod && (
    <ListItem iconFront="business_center">
      {i18n.t("component.talent_details.notice_period")}
      {" • "}
      <span class="color-brand-jade">
        {i18n.t("component.talent_details.notice_period_value", {
          count: user.noticePeriod,
        })}
      </span>
    </ListItem>
  );

  const additionalInfo3 = (previewMode || showCompensationRange(user)) && (
    <ListItem iconFront="payments">
      {i18n.t("profile_details.compensation_range")} •{" "}
      <CompensationRange
        previewMode={previewMode}
        class="ml-8"
        range={user.compensationRange}
      />
    </ListItem>
  );

  function handleAddExperience(event) {
    event.preventDefault();
    setShowExperienceModal(true);
  }

  function handleEditExperience(experience) {
    setShowExperienceModal(true);
    setEditExperience(experience);
  }

  function receiveNewExperience(experience) {
    addOrUpdateExperience(experience);
  }

  return (
    <div class={classNames("talent-details")}>
      {((user.workExperiences && user.workExperiences.length > 0) ||
        isSelf) && (
        <div class="talent-details__section">
          <h2 class="t-h6 t-h6--margin">
            {i18n.t("profile_details.work_experience.title")}
            {isSelf && (
              <CircularButton
                circular
                small
                grey
                flat
                class="ml-12"
                trackingName="addExperience"
                onClick={handleAddExperience}
              >
                add
              </CircularButton>
            )}
          </h2>
          {user.workExperiences.length === 0 && (
            <p class="talent-details__empty-text">
              {i18n.t("profile_details.work_experience.empty")}
            </p>
          )}
          {sortWorkExperiences(user.workExperiences).map((exp) => (
            <WorkExperience
              experience={exp}
              previewMode={previewMode}
              editMode={isSelf}
              onEdit={handleEditExperience}
            />
          ))}
        </div>
      )}

      {user.languageSkills && (
        <div class="talent-details__section">
          <h2 class="t-h6 t-h6--margin">
            {i18n.t("profile_details.languages")}
          </h2>
          {user.languageSkills.map(({ value, proficiency }) => (
            <ListItem
              bold
              secondLine={
                <Fragment>
                  <span>{firstCharUppercase(proficiency)}</span>
                  {getValue(value) == "de" && cefr && " • "}
                  {getValue(value) == "de" && cefr && (
                    <span class="color-brand-jade">{cefr}</span>
                  )}
                </Fragment>
              }
            >
              {getText(value)}
            </ListItem>
          ))}
        </div>
      )}

      <div class="talent-details__section">
        <h2 class="t-h6 t-h6--margin">
          {i18n.t("profile_details.eligibility")}
        </h2>
        {elibilityText && (
          <p>
            {renderHtml(replaceRedactions(replaceVariables(elibilityText)))}
          </p>
        )}

        <div class="mt-32 mb-32">
          <ListItem
            unpadded
            iconFront={
              <CountryFlag class="mr-24" code={getValue(user.citizenship)} />
            }
            secondLine={i18n.t("profile_details.citizenship")}
          >
            {getText(user.citizenship)}
          </ListItem>
          {user.secondCitizenship && (
            <ListItem
              iconFront={
                <CountryFlag
                  class="mr-24"
                  code={getValue(user.secondCitizenship)}
                />
              }
              secondLine={i18n.t("profile_details.second_citizenship")}
            >
              {getText(user.secondCitizenship)}
            </ListItem>
          )}
        </div>

        <QualificationDisplay user={user} />
      </div>

      {(additionalInfo0 ||
        additionalInfo1 ||
        additionalInfo2 ||
        additionalInfo3) && (
        <div class="talent-details__section">
          <h2 class="t-h6 t-h6--margin">
            {i18n.t("component.talent_details.additional")}
          </h2>

          {additionalInfo0}
          {additionalInfo1}
          {additionalInfo2}
          {additionalInfo3}
        </div>
      )}

      <div>
        {(user.relocationMotivation || user.keyMotivatorsRecognition) && (
          <div class="pt-32">
            <h2 class="t-h6 t-h6--margin">
              {i18n.t("component.talent_details.key_motivators")}
            </h2>
            {user.relocationMotivation && (
              <p>
                <h4 class="t-body3 mb-12">
                  {i18n.t("component.talent_details.relocating_drivers")}
                </h4>

                {user.relocationMotivation.sort().map((motivation) => (
                  <Tag noHover grey class="mr-12">
                    {motivation}
                  </Tag>
                ))}
              </p>
            )}
            {user.keyMotivatorsRecognition && (
              <div class="talent-details__key-motivators">
                <span class="t-caption text-dimmed">
                  {i18n.t("questions.key_motivators_recognition.short")}
                  <br />
                  <Rating value={user.keyMotivatorsRecognition / 2} />
                </span>
                <span class="t-caption text-dimmed">
                  {i18n.t("questions.key_motivators_compensation.short")}
                  <br />
                  <Rating value={user.keyMotivatorsCompensation / 2} />
                </span>
                <span class="t-caption text-dimmed">
                  {i18n.t("questions.key_motivators_growth.short")}
                  <br />
                  <Rating value={user.keyMotivatorsGrowth / 2} />
                </span>
                <span class="t-caption text-dimmed">
                  {i18n.t("questions.key_motivators_purpose.short")}
                  <br />
                  <Rating value={user.keyMotivatorsPurpose / 2} />
                </span>
              </div>
            )}
          </div>
        )}

        {user.jobComment && (
          <div>
            <h6 class="t-subtitle1 mt-48 mb-8">
              {i18n.t("component.talent_details.job_comment")}
            </h6>
            {previewMode ? (
              <RedactedInfo lines={3} />
            ) : (
              renderUserHtml(user.jobComment, "p")
            )}
          </div>
        )}
      </div>

      {previewMode && (
        <div class="bg-shades-bright-grey border-top-gainsboro border-bottom-gainsboro pl-16 pr-16 mt-48">
          <ListItem
            iconBack="file_download"
            iconFront="picture_as_pdf"
            secondLine={<RedactedInfo darker small w40 />}
            download={`resume_xxx.pdf`}
            onClick={handleClickWhenLoggedIn(trackResumeClick)}
          >
            <RedactedInfo darker w160 />
          </ListItem>
        </div>
      )}

      {!previewMode && user.resume && (
        <div class="bg-shades-bright-grey border-top-gainsboro border-bottom-gainsboro pl-16 pr-16 mt-48">
          <ListItem
            iconBack="file_download"
            iconFront="picture_as_pdf"
            secondLine={`${`resume_${stringTofileName(
              user.name
            )}.pdf`} – ${humanFileSize(user.resume.fileSize)}`}
            link={user.resume.url}
            download={`resume_${stringTofileName(user.name)}.pdf`}
            onClick={trackResumeClick}
          >
            {i18n.t("profile_details.resume")}
          </ListItem>
        </div>
      )}
      {!previewMode && user.resume && user.showResumeHint && (
        <p class="t-caption color-shades-charcoal">
          {i18n.t("profile_details.resume_hint")}
        </p>
      )}

      <div class="talent-details__section">
        {(user.email || user.phone) && (
          <h2 class="t-h6 t-h6--margin">{i18n.t("profile_details.contact")}</h2>
        )}
        {user.email && <ListItem iconFront="email">{user.email}</ListItem>}
        {user.phone && <ListItem iconFront="phone">{user.phone}</ListItem>}
      </div>
      {showExperienceModal && (
        <div class="nioomi-modal-wrapper">
          <AddEditExperienceModal
            experience={editExperience}
            onClose={() => {
              setShowExperienceModal(false);
              setEditExperience(null);
            }}
            opened={showExperienceModal}
            afterSubmit={receiveNewExperience}
            afterDelete={deleteExperience}
          ></AddEditExperienceModal>
        </div>
      )}
    </div>
  );
}
